import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat.js';
import relativeTime from 'dayjs/plugin/relativeTime.js';
import customParseFormat from 'dayjs/plugin/customParseFormat.js';
import utc from 'dayjs/plugin/utc.js';
import timezone from 'dayjs/plugin/timezone.js'; // dependent on utc plugin
import 'dayjs/locale/de.js';
import 'dayjs/locale/en.js';
import 'dayjs/locale/cs.js';
import 'dayjs/locale/nl.js';
export class DateService {
    constructor(date, format = null, locale = 'de') {
        this.defaultTimezone = 'Europe/Berlin';
        dayjs.locale(locale);
        dayjs.extend(relativeTime);
        dayjs.extend(localizedFormat);
        dayjs.extend(customParseFormat); // Required for tracker times on which we input a localized format
        // Timezone support
        dayjs.extend(utc);
        dayjs.extend(timezone);
        this.date = this.getDayJsInstance(date, format);
    }
    /**
     * Gets or sets the hour.
     * Accepts numbers from 0 to 23. If the range is exceeded, it will bubble up to the day.
     *
     * @param hour
     * @returns {DateService|number}
     */
    hour(hour = undefined) {
        if (hour !== undefined) {
            this.date = this.date.hour(hour);
            return this;
        }
        return this.date.hour();
    }
    /**
     * Gets or sets the minutes.
     * Accepts numbers from 0 to 59. If the range is exceeded, it will bubble up to the hour.
     *
     * @param minute
     * @returns {DateService|number}
     */
    minute(minute = undefined) {
        if (minute !== undefined) {
            this.date = this.date.minute(minute);
            return this;
        }
        return this.date.minute();
    }
    /**
     * Gets or sets the second.
     * Accepts numbers from 0 to 59. If the range is exceeded, it will bubble up to the minutes.
     *
     * @param second
     * @returns {DateService|number}
     */
    second(second = undefined) {
        if (second !== undefined) {
            this.date = this.date.second(second);
            return this;
        }
        return this.date.second();
    }
    /**
     * Gets or sets the milliseconds.
     * Accepts numbers from 0 to 999. If the range is exceeded, it will bubble up to the seconds.
     *
     * @param ms
     * @returns {DateService|number}
     */
    millisecond(ms = undefined) {
        if (ms !== undefined) {
            this.date = this.date.millisecond(ms);
            return this;
        }
        return this.date.millisecond();
    }
    /**
     * Gets or sets the day of the month.
     * Accepts numbers from 1 to 31. If the range is exceeded, it will bubble up to the months.
     *
     * @param day
     * @returns {DateService|number}
     */
    day(day = undefined) {
        if (day !== undefined) {
            this.date = this.date.date(day);
            return this;
        }
        return this.date.date();
    }
    /**
     * Gets or sets the week day number.
     * Accepts numbers from 1 (Monday) to 7 (Sunday). If the range is exceeded, it will bubble up to other weeks.
     *
     * @param weekDayNumber
     * @returns {DateService|number}
     */
    weekDay(weekDayNumber = undefined) {
        if (weekDayNumber !== undefined) {
            this.date = this.date.day(weekDayNumber === 7 ? 0 : weekDayNumber);
            return this;
        }
        let weekDay = this.date.day();
        return weekDay === 0 ? 7 : weekDay;
    }
    /**
     * Gets or sets the month number.
     * 1 = January
     * ...
     * 12 = December
     *
     * @param monthNumber
     * @returns {DateService|number}
     */
    month(monthNumber = undefined) {
        if (monthNumber !== undefined) {
            this.date = this.date.month(monthNumber - 1);
            return this;
        }
        return this.date.month() + 1;
    }
    /**
     * Gets or sets the year.
     *
     * @param year
     * @returns {DateService|number}
     */
    year(year = undefined) {
        if (year !== undefined) {
            this.date = this.date.year(year);
            return this;
        }
        return this.date.year();
    }
    /**
     * Gets or sets the time.
     *
     * @param {string} time
     * @returns {DateService|string}
     */
    time(time = undefined) {
        if (time !== undefined) {
            const preciseParts = time.split('.');
            const timeParts = preciseParts[0].split(':');
            this.hour(parseInt(timeParts[0]));
            this.minute(parseInt(timeParts[1]));
            this.second(timeParts.length > 2 ? parseInt(timeParts[2]) : 0);
            this.millisecond(preciseParts.length > 1 ? parseInt(preciseParts[1]) : 0);
            return this;
        }
        return this.format('LTS');
    }
    format(pattern) {
        return this.date.format(pattern);
    }
    /**
     * Formats the date as a date range string.
     *
     * @param {string|this} end A date to mark the end of the date range.
     * @param {string} format
     * @returns {string}
     */
    formatDateRange(end, format = 'DD. MMM YYYY') {
        let start = this.date;
        end = this.getDayJsInstance(end);
        if (start.date() === end.date() && start.month() === end.month() && start.year() === end.year()) {
            return start.format(format);
        }
        let formatParts = format.split(' ');
        if (start.month() === end.month() && start.year() === end.year()) {
            return start.format(formatParts[0]) + ' - ' + end.format(format);
        }
        if (start.year() === end.year()) {
            return start.format(formatParts[0] + ' ' + formatParts[1]) + ' - ' + end.format(format);
        }
        return start.format(format) + ' - ' + end.format(format);
    }
    /**
     * Formats the date as a date time range string.
     *
     * @param {string|boolean|this} end A date to mark the end of the date range or a boolean with {@code true} to set the instances date as end date with an open start or {@code false} to set the instances date as start date with an open end.
     * @param {boolean} forceTime
     * @returns {string}
     */
    formatDateTimeRange(end, forceTime = false) {
        const start = this.date;
        const now = this.getDayJsInstance();
        const startTime = start.format('LT');
        const startInPast = start.isBefore(now);
        if (end === true || end === false) {
            let text = start.format('DD. MMMM YYYY');
            // Do not show the time part, when the time is not relevant
            if (forceTime || (!startInPast && ((!end && startTime !== '00:00') || (end && startTime !== '23:59')))) {
                text += ` ${startTime}`;
            }
            return text;
        }
        end = this.getDayJsInstance(end);
        if (start.date() === end.date() && start.month() === end.month() && start.year() === end.year()) {
            return `${start.format('LL')} ${start.format('LT')} - ${end.format('LT')}`;
        }
        const endTime = end.format('LT');
        const endInPast = end.isBefore(now);
        const isSameYear = start.year() === end.year();
        let text = start.format(isSameYear ? 'DD. MMM' : 'DD. MMM YYYY');
        // Do not show the time part, when the time is not relevant
        if (forceTime || (!startInPast && startTime !== '00:00')) {
            text += ` ${startTime}`;
        }
        text += ' - ';
        text += end.format('DD. MMM YYYY');
        // Do not show the time part, when the time is not relevant
        if (forceTime || (!endInPast && endTime !== '23:59')) {
            text += ` ${endTime}`;
        }
        return text;
    }
    /**
     * Returns a friendly readable 'fromNow()' date string when the date difference to now is not older than 2 days.
     *
     * @returns {string}
     */
    calendar(format = 'LLL', diffAmount = 3, diffUnit = 'days') {
        return this.diff(undefined, diffUnit) < diffAmount ? this.date.fromNow() : this.date.format(format);
    }
    fromNow() {
        return this.date.fromNow();
    }
    isValid() {
        return this.date.isValid();
    }
    startOf(unit) {
        this.date = this.date.startOf(unit);
        return this;
    }
    endOf(unit) {
        this.date = this.date.endOf(unit);
        return this;
    }
    isSameDay(otherDate) {
        return this.isSame(otherDate, false);
    }
    isSame(otherDate, compareTime = true) {
        const date = this.date;
        otherDate = this.getDayJsInstance(otherDate);
        return date.isSame(otherDate, compareTime ? 'second' : 'day');
    }
    isBefore(otherDate, compareTime = true) {
        const date = this.date;
        otherDate = this.getDayJsInstance(otherDate);
        return date.isBefore(otherDate, compareTime ? 'second' : 'day');
    }
    isAfter(otherDate, compareTime = true) {
        let date = this.date;
        otherDate = this.getDayJsInstance(otherDate);
        return date.isAfter(otherDate, compareTime ? 'second' : 'day');
    }
    isSameOrBefore(otherDate, compareTime = true) {
        return this.isSame(otherDate, compareTime) || this.isBefore(otherDate, compareTime);
    }
    isSameOrAfter(otherDate, compareTime = true) {
        return this.isSame(otherDate, compareTime) || this.isAfter(otherDate, compareTime);
    }
    /**
     * Indicates the difference between two date-time in the specified unit.
     *
     * @param otherDate
     * @param unit Is case-insensitive, and support plural and short forms.
     * @param absolute
     * @returns {number}
     */
    diff(otherDate = undefined, unit = 'millisecond', absolute = true) {
        otherDate = this.getDayJsInstance(otherDate);
        let diff = this.date.diff(otherDate, unit);
        return absolute ? Math.abs(diff) : diff;
    }
    timeDiff(otherDate = undefined) {
        const secondsDiff = this.diff(otherDate, 'seconds');
        const hours = Math.floor(secondsDiff / 60 / 60);
        const minutes = Math.floor((secondsDiff - (hours * 60 * 60)) / 60);
        const seconds = Math.floor((secondsDiff - (hours * 60 * 60) - (minutes * 60)));
        return this.time(`${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`).time();
    }
    /**
     * Gets the difference of the date to the current date, i.e., the age
     *
     * @returns {number}
     */
    age() {
        return Math.abs(this.diff(undefined, 'years'));
    }
    /**
     * Returns a cloned instance with a specified amount of time added.
     *
     * @param amount
     * @param unit Is case-insensitive, and support plural and short forms.
     * @returns {DateService}
     */
    add(amount, unit) {
        this.date = this.date.add(amount, unit);
        return this;
    }
    /**
     * Returns a cloned instance with a specified amount of time subtracted.
     *
     * @param amount
     * @param unit Is case-insensitive, and support plural and short forms.
     * @returns {DateService}
     */
    subtract(amount, unit) {
        this.date = this.date.subtract(amount, unit);
        return this;
    }
    /**
     * Returns the Unix timestamp (the number of **seconds** since the Unix Epoch) of the Day.js object.
     * ```
     * dayjs('2019-01-25').unix() // 1548381600
     * ```
     * This value is floored to the nearest second, and does not include a milliseconds component.
     */
    unix() {
        return this.date.unix();
    }
    /**
     * Parses the specified date (optionally in the specified format) into a Day.js object.
     * We ensure that the timezone is correctly set to the local timezone (currently only Europe/Berlin) as formats would be returned in UTC otherwise
     * since it's forced by AWS Lambda.
     */
    getDayJsInstance(date = undefined, format = null) {
        if (date && date instanceof DateService) {
            return date.date.tz(this.defaultTimezone);
        }
        // If the date is a number, we natively expect it to be a unix timestamp
        if (!isNaN(date)) {
            return dayjs.unix(date);
        }
        if (format) {
            return dayjs(date, format, true).tz(this.defaultTimezone);
        }
        return dayjs(date).tz(this.defaultTimezone);
    }
    toDate() {
        return this.date.toDate();
    }
}
//# sourceMappingURL=DateService.js.map